import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Building2, UserRound, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusChip } from "@/components/StatusChip";
import { StagePipeline } from "@/components/StagePipeline";
import { FeedbackCard } from "@/components/case/FeedbackCard";
import { EntryRequestSection } from "@/components/case/EntryRequestSection";
import { PlanSection } from "@/components/case/PlanSection";
import { CheckInsSection } from "@/components/case/CheckInsSection";
import { DecisionSection } from "@/components/case/DecisionSection";
import { Timeline } from "@/components/case/Timeline";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { statusMeta } from "@/lib/format";

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ["case", id],
    queryFn: async () => (await api.get(`/cases/${id}`)).data,
  });

  if (isLoading) return <div className="space-y-4"><Skeleton className="h-24 rounded-2xl" /><Skeleton className="h-64 rounded-2xl" /></div>;
  if (isError || !c) return (
    <div className="text-center py-16">
      <p className="text-patria-ink font-medium">Case not found or access denied.</p>
      <button onClick={() => navigate("/cases")} className="text-patria-green text-sm mt-2">Back to cases</button>
    </div>
  );

  const sm = statusMeta(c.status);

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-sm text-patria-muted hover:text-patria-ink" data-testid="case-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header */}
      <Card className="bg-patria-surface border-patria-muted/15 p-6" data-testid="case-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-semibold text-patria-ink" data-testid="case-employee-name">{c.employee?.name}</h1>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-sm text-patria-muted">
              <span className="inline-flex items-center gap-1.5"><UserRound className="h-4 w-4" />{c.employee?.title}</span>
              <span className="inline-flex items-center gap-1.5"><Building2 className="h-4 w-4" />{c.campus}</span>
              <span>Manager: {c.manager?.name}</span>
            </div>
          </div>
          <StatusChip label={sm.label} tone={sm.tone} testId="case-header-status" />
        </div>

        {(c.overdue_count > 0 || c.past_review) && (
          <div className="mt-4 flex items-center gap-2 rounded-xl bg-patria-terracotta/10 px-4 py-2.5 text-sm font-medium text-patria-terracottaDeep" data-testid="case-alert">
            <AlertTriangle className="h-4 w-4" />
            {c.overdue_count > 0 && `${c.overdue_count} overdue check-in${c.overdue_count > 1 ? "s" : ""}. `}
            {c.past_review && "Plan is past its final-review date."}
          </div>
        )}

        <div className="mt-6 overflow-x-auto pb-1">
          <StagePipeline status={c.status} />
        </div>
      </Card>

      {/* Stages */}
      <FeedbackCard feedback={c.feedback} />
      <EntryRequestSection c={c} me={user} />
      <PlanSection c={c} me={user} />
      <CheckInsSection c={c} me={user} />
      <DecisionSection c={c} me={user} />

      {/* Audit trail */}
      <Card className="bg-patria-surface border-patria-muted/15 p-6">
        <h3 className="font-heading font-semibold text-patria-ink mb-4">Audit trail</h3>
        <Timeline audit={c.audit} />
      </Card>
    </div>
  );
}
